import React, { forwardRef, Ref } from 'react';
import { UseFormRegister, FieldErrors, FieldValues, RegisterOptions, Path } from 'react-hook-form';

interface DropdownProps<T extends FieldValues = FieldValues> {
  id: string;
  name: Path<T>;
  label: string;
  options: { value: string; label: string }[];
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  validationRules?: RegisterOptions<T, Path<T>>;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  defaultValue?: string;
  disabled?: boolean;
}

/**
 * Dropdown Component
 *
 * A reusable generic dropdown component with label and error message
 */
function DropdownInner<T extends FieldValues = FieldValues>(
  {
    id,
    name,
    label,
    options,
    register,
    errors,
    validationRules,
    placeholder = 'Select an option',
    className = '',
    fullWidth = true,
    defaultValue,
    disabled = false,
  }: DropdownProps<T>,
  ref: Ref<HTMLSelectElement>
) {
  // Get error message from form errors
  const errorMessage = errors[name]?.message as string;

  // Base classes
  // text-base (16px) on mobile prevents iOS focus-zoom; sm: restores the desktop look.
  const baseClasses = 'bg-white rounded-lg border px-3 py-2.5 text-base sm:py-2 sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0';

  // Error classes
  const errorClasses = errorMessage
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500';

  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';

  // Disabled classes
  const disabledClasses = disabled ? 'bg-gray-100 cursor-not-allowed' : '';

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <select
        id={id}
        {...register(name, validationRules)}
        className={`${baseClasses} ${errorClasses} ${widthClasses} ${disabledClasses} ${className}`}
        disabled={disabled}
        defaultValue={defaultValue}
      >
        {placeholder && (
          <option value="">{placeholder}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
}

// forwardRef wrapper with generic support via type assertion
const Dropdown = forwardRef(DropdownInner) as <T extends FieldValues = FieldValues>(
  props: DropdownProps<T> & { ref?: Ref<HTMLSelectElement> }
) => React.ReactElement | null;

export default Dropdown;
